import { format } from 'date-fns';
import { createZipBlob, type ZipEntryInput } from './createZipBlob';
import { triggerProgrammaticDownload } from './triggerProgrammaticDownload';

/** From this many files a download is one archive instead of one browser download per file. */
export const ZIP_DOWNLOAD_MIN_FILES = 3;

export const shouldDownloadAsZip = (fileCount: number): boolean => fileCount >= ZIP_DOWNLOAD_MIN_FILES;

export interface ZipDownloadEntry {
  /** Name the file gets inside the archive; duplicates are numbered rather than overwritten. */
  filename: string;
  /** Resolves the download URL right before fetching, so signed URLs don't expire. */
  resolveUrl: () => Promise<string | undefined>;
}

export interface ZipDownloadResult {
  /** Files left out of the archive. Callers own how these are reported. */
  failedFilenames: string[];
}

/** Fetch a few files at a time. */
const FETCH_CONCURRENCY = 4;

/** Characters that would create folders or aren't allowed on Windows. */
// eslint-disable-next-line no-control-regex
const UNSAFE_FILENAME_CHARS = /[\\/:*?"<>|\x00-\x1f]/g;

const sanitizeEntryName = (name: string): string => name.replace(UNSAFE_FILENAME_CHARS, '_').trim() || 'file';

/** Makes entry names unique (`Letter (2).pdf`), case-insensitively. */
const toUniqueEntryName = (name: string, taken: Set<string>): string => {
  const safeName = sanitizeEntryName(name);
  if (!taken.has(safeName.toLowerCase())) {
    taken.add(safeName.toLowerCase());
    return safeName;
  }

  const extensionIndex = safeName.lastIndexOf('.');
  const stem = extensionIndex > 0 ? safeName.slice(0, extensionIndex) : safeName;
  const extension = extensionIndex > 0 ? safeName.slice(extensionIndex) : '';

  let suffix = 2;
  let candidate = `${stem} (${suffix})${extension}`;
  while (taken.has(candidate.toLowerCase())) {
    suffix += 1;
    candidate = `${stem} (${suffix})${extension}`;
  }
  taken.add(candidate.toLowerCase());
  return candidate;
};

/** `Smith v Jones` + today → `Smith_v_Jones_2026-09-03.zip`. */
export const zipDownloadFilename = (baseName: string, now: Date = new Date()): string => {
  const safeBase = sanitizeEntryName(baseName).replace(/\s+/g, '_');
  return `${safeBase}_${format(now, 'yyyy-MM-dd')}.zip`;
};

const fetchFileBlob = async (url: string): Promise<Blob> => {
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(`Download failed with status ${response.status}`);
  }
  return response.blob();
};

/**
 * Fetches all entries and saves one archive. Failed files are skipped and returned in
 * `failedFilenames`. Nothing is saved if every file fails. Data stays in the blob store (see
 * `createZipBlob`).
 */
export const downloadFilesAsZip = async (
  entries: ZipDownloadEntry[],
  zipFilename: string,
): Promise<ZipDownloadResult> => {
  const results = new Array<Blob | undefined>(entries.length);
  let nextIndex = 0;

  const fetchNext = async (): Promise<void> => {
    while (nextIndex < entries.length) {
      const index = nextIndex;
      nextIndex += 1;
      try {
        const url = await entries[index].resolveUrl();
        results[index] = url ? await fetchFileBlob(url) : undefined;
      } catch {
        results[index] = undefined;
      }
    }
  };

  await Promise.all(Array.from({ length: Math.min(FETCH_CONCURRENCY, entries.length) }, fetchNext));

  const takenNames = new Set<string>();
  const zipEntries: ZipEntryInput[] = [];
  const failedFilenames: string[] = [];

  entries.forEach((entry, index) => {
    const blob = results[index];
    if (!blob) {
      failedFilenames.push(entry.filename);
      return;
    }
    zipEntries.push({ name: toUniqueEntryName(entry.filename, takenNames), blob });
  });

  if (zipEntries.length === 0) {
    return { failedFilenames };
  }

  const zipBlob = await createZipBlob(zipEntries);
  const zipUrl = URL.createObjectURL(zipBlob);
  try {
    triggerProgrammaticDownload(zipUrl, zipFilename);
  } finally {
    URL.revokeObjectURL(zipUrl);
  }

  return { failedFilenames };
};
